import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import i18n from 'i18next';
import { enqueueSnackbar } from 'notistack';
import {
  closeAddCardDialog,
  closeAddNewCompetencyTypeDialog,
  closeCardDialog,
  closeDeleteCardDialog,
  closeEditCardDialog,
  closeManageAddedCompetenciesDialog,
  openAddCardDialog,
  openAddCardDialogForOtherUser,
  openAddNewCompetencyTypeDialog,
  openCardDialogWithUserId,
  openDeleteCardDialog,
  openEditCardDialog,
  openManageAddedCompetenciesDialog,
  resetCardsByUserId,
  setCardTypeToAdd,
  setIsCardToAddWithMultipleImages,
} from './actions/cards.actions';
import { addCardForCurrentUser } from './thunks/addCardForCurrentUserThunk';
import { addCardForOtherUser } from './thunks/addCardForOtherUser.thunk';
import { addNewCompetencyType } from './thunks/addNewCompetencyType.thunk';
import { deleteCard } from './thunks/deleteCard.thunk';
import { deleteCompetencyType } from './thunks/deleteCompetencyType.thunk';
import { loadBusinessCompetencyTypes } from './thunks/loadBusinessCompetencyTypes.thunk';
import { loadCurrentUserCards } from './thunks/loadCurrentUserCards.thunk';
import { loadExpiringCardInfoOfEmployeesOfEmployer } from './thunks/loadExpiringCardInfoOfEmployeesInBusiness.thunk';
import { loadExpiringCardsOfOwnBusinessEmployees } from './thunks/loadExpiringCardsOfOwnBusinessEmployees.thunk';
import { loadValidCardsOfMultipleUsers } from './thunks/loadValidCardsOfMultipleUsers.thunk';
import { loadValidCardsOfUser } from './thunks/loadValidCardsOfUser.thunk';
import { updateCard } from './thunks/updateCard.thunk';

const cardsAdapter = createEntityAdapter();

const expiringEmployeeCardsAdapter = createEntityAdapter();

const competencyTypesAdapter = createEntityAdapter();

const updateCardsByUser = (state, card, action = 'add') => {
  const userId = card.userId;
  if (!userId) return;

  if (!state.cardsByUser[userId]) {
    state.cardsByUser[userId] = [];
  }

  if (action === 'add') {
    const existingCardIndex = state.cardsByUser[userId].findIndex(
      (c) => c.id === card.id
    );
    if (existingCardIndex === -1) {
      state.cardsByUser[userId].push(card);
    } else {
      state.cardsByUser[userId][existingCardIndex] = card;
    }
  } else if (action === 'remove') {
    state.cardsByUser[userId] = state.cardsByUser[userId].filter(
      (c) => c.id !== card.id
    );
  }
};

const cardsSlice = createSlice({
  name: 'cards',
  initialState: {
    cards: cardsAdapter.getInitialState(),
    competencyTypes: competencyTypesAdapter.getInitialState(),
    expiringEmployeeCards: expiringEmployeeCardsAdapter.getInitialState(),
    cardsByUser: {}, // Structure: { userId: [cards], userId2: [cards] }
    loadingCardsByUser: {}, // Structure: { userId: boolean }
    userIdToAddCardFor: '',
    cardTypeToAdd: '',
    cardIdToDelete: '',
    loading: false,
    validCardsLoaded: false,
    isAddCardForOtherUserDialogOpen: false,
    isAddingCard: false,
    isDeletingCard: false,
    userIdForCardDialog: '',
    isCardDialogOpen: false,
    isDeleteCardDialogOpen: false,
    isAddCardDialogOpen: false,
    isAddNewCompetencyTypeDialogOpen: false,
    isManageAddedCompetenciesDialogOpen: false,
    isAddingNewCompetencyType: false,
    isLoadingBusinessCompetencyTypes: false,
    isCardToAddWithMultipleImages: false,
    isEditCardDialogOpen: false,
    selectedCardIdToEdit: '',
    isUpdatingCard: false,
  },
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(loadCurrentUserCards.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadCurrentUserCards.fulfilled, (state, action) => {
        cardsAdapter.setMany(state.cards, action.payload);
        state.loading = false;
      })
      .addCase(loadCurrentUserCards.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(loadValidCardsOfUser.fulfilled, (state, action) => {
        cardsAdapter.setMany(state.cards, action.payload);
        state.validCardsLoaded = true;
      })
      .addCase(loadValidCardsOfMultipleUsers.pending, (state, action) => {
        state.loadingMultipleUsersCards = true;
        const userIds = action.meta.arg;
        userIds.forEach((userId) => {
          state.loadingCardsByUser[userId] = true;
        });
      })
      .addCase(loadValidCardsOfMultipleUsers.fulfilled, (state, action) => {
        state.loadingMultipleUsersCards = false;
        // action.payload is { userId: [cards] }
        Object.keys(action.payload).forEach((userId) => {
          state.cardsByUser[userId] = action.payload[userId];
          state.loadingCardsByUser[userId] = false;
        });
      })
      .addCase(loadValidCardsOfMultipleUsers.rejected, (state, action) => {
        state.loadingMultipleUsersCards = false;
        const userIds = action.meta.arg;
        userIds.forEach((userId) => {
          state.loadingCardsByUser[userId] = false;
        });
      })
      .addCase(resetCardsByUserId, (state, action) => {
        state.cardsByUser = {};
        state.loadingCardsByUser = {};
      })
      .addCase(addCardForCurrentUser.pending, (state, action) => {
        state.isAddingCard = true;
      })
      .addCase(addCardForCurrentUser.fulfilled, (state, action) => {
        const newCard = action.payload;
        cardsAdapter.setOne(state.cards, newCard);
        updateCardsByUser(state, newCard, 'add');

        state.isAddingCard = false;
        state.isAddCardDialogOpen = false;
        enqueueSnackbar('Kortti lisätty onnistuneesti', {
          variant: 'success',
        });
      })
      .addCase(addCardForCurrentUser.rejected, (state, action) => {
        state.isAddingCard = false;
        enqueueSnackbar('Kortin lisäys epäonnistui', {
          variant: 'error',
        });
      })
      .addCase(addCardForOtherUser.pending, (state, action) => {
        state.isAddingCard = true;
      })
      .addCase(addCardForOtherUser.fulfilled, (state, action) => {
        const newCard = action.payload;

        cardsAdapter.setOne(state.cards, newCard);
        updateCardsByUser(state, newCard, 'add');

        state.isAddingCard = false;
        state.isAddCardForOtherUserDialogOpen = false;
        state.userIdToAddCardFor = '';
        enqueueSnackbar('Kortti lisätty onnistuneesti', {
          variant: 'success',
        });
      })
      .addCase(addCardForOtherUser.rejected, (state, action) => {
        state.isAddingCard = false;
        enqueueSnackbar('Kortin lisäys epäonnistui', {
          variant: 'error',
        });
      })
      .addCase(openCardDialogWithUserId, (state, action) => {
        state.isCardDialogOpen = true;
        state.userIdForCardDialog = action.payload;
      })
      .addCase(closeCardDialog, (state, action) => {
        state.isCardDialogOpen = false;
        state.userIdForCardDialog = '';
      })
      .addCase(openAddCardDialog, (state, action) => {
        state.isAddCardDialogOpen = true;
      })

      .addCase(openAddCardDialogForOtherUser, (state, action) => {
        state.isAddCardForOtherUserDialogOpen = true;
        state.userIdToAddCardFor = action.payload;
      })

      .addCase(closeAddCardDialog, (state, action) => {
        state.isAddCardDialogOpen = false;
        state.isAddCardForOtherUserDialogOpen = false;
        state.userIdToAddCardFor = '';
        state.cardTypeToAdd = '';
      })
      .addCase(
        loadExpiringCardInfoOfEmployeesOfEmployer.fulfilled,
        (state, action) => {
          expiringEmployeeCardsAdapter.setMany(
            state.expiringEmployeeCards,
            action.payload
          );
        }
      )
      .addCase(
        loadExpiringCardsOfOwnBusinessEmployees.fulfilled,
        (state, action) => {
          expiringEmployeeCardsAdapter.setMany(
            state.expiringEmployeeCards,
            action.payload
          );
        }
      )
      .addCase(openDeleteCardDialog, (state, action) => {
        state.isDeleteCardDialogOpen = true;

        state.cardIdToDelete = action.payload;
      })
      .addCase(closeDeleteCardDialog, (state, action) => {
        state.isDeleteCardDialogOpen = false;
        state.cardIdToDelete = '';
      })
      .addCase(deleteCard.pending, (state, action) => {
        state.isDeletingCard = true;
      })
      .addCase(deleteCard.fulfilled, (state, action) => {
        const deletedCard = action.payload;

        // Update main cards state
        cardsAdapter.setOne(state.cards, deletedCard);

        updateCardsByUser(state, deletedCard, 'remove');

        state.isDeleteCardDialogOpen = false;
        state.cardIdToDelete = '';
        state.isDeletingCard = false;
        enqueueSnackbar(i18n.t('notifications.success.delete-card-success'), {
          variant: 'success',
        });
      })
      .addCase(deleteCard.rejected, (state, action) => {
        state.isDeletingCard = false;
        enqueueSnackbar(i18n.t('notifications.errors.delete-card-rejected'), {
          variant: 'error',
        });
      })
      .addCase(loadBusinessCompetencyTypes.pending, (state, action) => {
        state.isLoadingBusinessCompetencyTypes = true;
      })
      .addCase(loadBusinessCompetencyTypes.fulfilled, (state, action) => {
        competencyTypesAdapter.setAll(state.competencyTypes, action.payload);
        state.isLoadingBusinessCompetencyTypes = false;
      })
      .addCase(loadBusinessCompetencyTypes.rejected, (state, action) => {
        state.isLoadingBusinessCompetencyTypes = false;
      })
      .addCase(deleteCompetencyType.fulfilled, (state, action) => {
        competencyTypesAdapter.removeOne(
          state.competencyTypes,
          action.payload.id
        );
        state.cardTypeToAdd = '';
        enqueueSnackbar('Pätevyystyyppi poistettu onnistuneesti', {
          variant: 'success',
        });
      })
      .addCase(setCardTypeToAdd, (state, action) => {
        state.cardTypeToAdd = action.payload;
      })
      .addCase(openAddNewCompetencyTypeDialog, (state, action) => {
        state.isAddNewCompetencyTypeDialogOpen = true;
      })
      .addCase(closeAddNewCompetencyTypeDialog, (state, action) => {
        state.isAddNewCompetencyTypeDialogOpen = false;
      })
      .addCase(addNewCompetencyType.pending, (state, action) => {
        state.isAddingNewCompetencyType = true;
      })
      .addCase(addNewCompetencyType.fulfilled, (state, action) => {
        const createdBusinessCompetencies = action.payload;

        competencyTypesAdapter.setMany(
          state.competencyTypes,
          createdBusinessCompetencies
        );
        state.cardTypeToAdd = `${createdBusinessCompetencies[0].type}#${createdBusinessCompetencies[0].name}`;
        state.isAddNewCompetencyTypeDialogOpen = false;
        state.isAddingNewCompetencyType = false;

        enqueueSnackbar(
          i18n.t('notifications.success.add_competency_type_success'),
          {
            variant: 'success',
          }
        );
      })
      .addCase(addNewCompetencyType.rejected, (state, action) => {
        enqueueSnackbar(
          i18n.t('notifications.success.add_competency_type_rejected'),
          {
            variant: 'error',
          }
        );
        state.isAddingNewCompetencyType = false;
      })
      .addCase(openManageAddedCompetenciesDialog, (state, action) => {
        state.isManageAddedCompetenciesDialogOpen = true;
      })
      .addCase(closeManageAddedCompetenciesDialog, (state, action) => {
        state.isManageAddedCompetenciesDialogOpen = false;
      })
      .addCase(setIsCardToAddWithMultipleImages, (state, action) => {
        state.isCardToAddWithMultipleImages = action.payload;
      })
      .addCase(openEditCardDialog, (state, action) => {
        state.isEditCardDialogOpen = true;
        state.selectedCardIdToEdit = action.payload;
      })
      .addCase(closeEditCardDialog, (state, action) => {
        state.isEditCardDialogOpen = false;
        state.selectedCardIdToEdit = null;
      })
      .addCase(updateCard.pending, (state, action) => {
        state.isUpdatingCard = true;
      })
      .addCase(updateCard.fulfilled, (state, action) => {
        const updatedCard = action.payload;
        cardsAdapter.setOne(state.cards, updatedCard);

        state.isUpdatingCard = false;
        state.isEditCardDialogOpen = false;
        state.selectedCardIdToEdit = '';
        enqueueSnackbar('Kortti päivitetty onnistuneesti', {
          variant: 'success',
        });
      })
      .addCase(updateCard.rejected, (state, action) => {
        state.isUpdatingCard = false;
        enqueueSnackbar('Kortin päivitys epäonnistui', {
          variant: 'error',
        });
      });
  },
});

export default cardsSlice.reducer;
